import { $prose } from '@milkdown/kit/utils';
import { keymap } from '@milkdown/kit/prose/keymap';
import type { EditorView } from '@milkdown/kit/prose/view';
import type { KeyboardEvent } from 'react';

import { CONTENT_VARIANT_NODE_NAME } from './constants';
import { selectAndFocusContentVariantBody } from './utils';

/** Focusable controls inside variable-content toolbars (channel select, delete, Plus). */
const CONTROL_FOCUSABLE_SELECTOR = '[role="combobox"], button:not([disabled]), select:not([disabled])';

function getVariableContentRoot(el: Element | null): HTMLElement | null {
  if (!el) {
    return null;
  }

  const root = el.closest('[data-variable-content]');

  return root instanceof HTMLElement ? root : null;
}

/**
 * Keyboard order for the whole block: each variant's toolbar controls, then the
 * header Plus button — so Tab can reach Add after the last variant.
 */
export function getBlockControlFocusables(vcRoot: HTMLElement): HTMLElement[] {
  const variantFocusables = Array.from(vcRoot.querySelectorAll<HTMLElement>('[data-variant-controls]')).flatMap((toolbar) =>
    Array.from(toolbar.querySelectorAll<HTMLElement>(CONTROL_FOCUSABLE_SELECTOR)),
  );

  const header = vcRoot.querySelector('[data-variable-content-controls]');
  const headerFocusables =
    header instanceof HTMLElement ? Array.from(header.querySelectorAll<HTMLElement>(CONTROL_FOCUSABLE_SELECTOR)) : [];

  return [...variantFocusables, ...headerFocusables];
}

/** First focusable in a variant toolbar (channel select). */
function getVariantControlFocusTarget(variantDom: HTMLElement): HTMLElement | null {
  const controls = variantDom.querySelector('[data-variant-controls]');
  if (!(controls instanceof HTMLElement)) {
    return null;
  }

  return controls.querySelector<HTMLElement>(CONTROL_FOCUSABLE_SELECTOR);
}

/**
 * Get the reference to the DOM element at the cursor position so they can be focused
 */
export function getVariantDomAtCursor(view: EditorView): HTMLElement | null {
  const { $from } = view.state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name !== CONTENT_VARIANT_NODE_NAME) {
      continue;
    }

    const pos = $from.before(depth);
    const dom = view.nodeDOM(pos);

    return dom instanceof HTMLElement ? dom : null;
  }

  return null;
}

/**
 * Focus the body of the variant DOM element.
 */
export function focusVariantDomBody(view: EditorView, variantDom: HTMLElement): void {
  const pos = view.posAtDOM(variantDom, 0);
  const $pos = view.state.doc.resolve(pos);
  for (let depth = $pos.depth; depth > 0; depth--) {
    if ($pos.node(depth).type.name === CONTENT_VARIANT_NODE_NAME) {
      selectAndFocusContentVariantBody(view, $pos.before(depth));

      return;
    }
  }

  const node = view.state.doc.nodeAt(pos);
  if (node?.type.name === CONTENT_VARIANT_NODE_NAME) {
    selectAndFocusContentVariantBody(view, pos);
  } else {
    view.focus();
  }
}

/** Leave the block control ring back into a variant body. */
function exitControlsToContent(view: EditorView, active: Element | null, prefer: 'near' | 'last'): void {
  const variantDom = active?.closest('[data-content-variant]');
  if (variantDom instanceof HTMLElement) {
    focusVariantDomBody(view, variantDom);

    return;
  }

  const vcRoot = getVariableContentRoot(active);
  const variants = vcRoot?.querySelectorAll('[data-content-variant]');
  if (!variants?.length) {
    view.focus();

    return;
  }

  const target = prefer === 'last' ? variants[variants.length - 1] : variants[0];
  if (target instanceof HTMLElement) {
    focusVariantDomBody(view, target);
  } else {
    view.focus();
  }
}

/**
 * Tab/Shift+Tab walks all controls in the variable_content block (variant toolbars,
 * then Plus). Esc / leaving either end returns focus to a variant body.
 */
export function handleBlockControlsKeyDown(event: KeyboardEvent<HTMLElement>, view: EditorView): void {
  if (event.key === 'Escape') {
    event.preventDefault();
    exitControlsToContent(view, document.activeElement, 'near');

    return;
  }

  if (event.key !== 'Tab') {
    return;
  }

  const vcRoot = getVariableContentRoot(event.currentTarget);
  if (!vcRoot) {
    return;
  }

  const focusables = getBlockControlFocusables(vcRoot);
  if (focusables.length === 0) {
    return;
  }

  const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const index = active ? focusables.indexOf(active) : -1;

  if (event.shiftKey) {
    if (index <= 0) {
      event.preventDefault();
      exitControlsToContent(view, active ?? event.currentTarget, 'near');

      return;
    }

    event.preventDefault();
    focusables[index - 1]?.focus();

    return;
  }

  if (index === -1 || index >= focusables.length - 1) {
    event.preventDefault();
    exitControlsToContent(view, active ?? event.currentTarget, 'last');

    return;
  }

  event.preventDefault();
  focusables[index + 1]?.focus();
}

/**
 * Tab from inside a content_variant moves focus to that variant's channel controls.
 * Further Tab moves across the whole variable_content control ring (see NodeView).
 */
export const variableContentKeymap = $prose(() => {
  return keymap({
    Tab: (_state, _dispatch, view) => {
      if (!view?.editable) {
        return false;
      }

      const variantDom = getVariantDomAtCursor(view);
      if (!variantDom) {
        return false;
      }

      const target = getVariantControlFocusTarget(variantDom);
      if (!target) {
        return false;
      }

      target.focus();

      return true;
    },
  });
});
